"use client";

import { useEffect, useRef } from "react";

import { trackContentView } from "@/lib/analytics";

// Scrolling past a section is not interest. Requiring the section to hold the
// screen for a few seconds is what separates a reader from a scroller.
const DWELL_MS = 3000;

// A quarter of the section on screen counts as looking at it. Sections taller
// than the viewport can never reach that ratio, so filling half the screen
// counts too — without this, tall sections would never report on mobile.
const VISIBLE_RATIO = 0.25;
const SCREEN_FILL_RATIO = 0.5;

const isBeingRead = (entry) =>
  entry.isIntersecting &&
  (entry.intersectionRatio >= VISIBLE_RATIO ||
    entry.intersectionRect.height >= window.innerHeight * SCREEN_FILL_RATIO);

/**
 * Reports a section as genuinely viewed once it has held the screen for
 * DWELL_MS. Returns a ref to attach to the section element.
 *
 * @param {string} contentName - Name reported to GTM, e.g. "AI Nexus".
 */
export function useContentView(contentName) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined") return;

    let dwellTimer = null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (isBeingRead(entry)) {
          if (dwellTimer) return;

          dwellTimer = setTimeout(() => {
            trackContentView({ contentName });
            // One report per mount; a soft navigation back here is a new view.
            observer.disconnect();
          }, DWELL_MS);
          return;
        }

        // Scrolled away before the dwell elapsed, so it does not count.
        clearTimeout(dwellTimer);
        dwellTimer = null;
      },
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
    );

    observer.observe(element);

    return () => {
      clearTimeout(dwellTimer);
      observer.disconnect();
    };
  }, [contentName]);

  return ref;
}
